import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DesignCardTile } from './design-card-tile.js';
import { useDesignCards } from '../../lib/design-board/use-design-board.js';

/**
 * What the customer already approved, shown on the order being made from it.
 *
 * The order has a board of its own — that is the production record, and it is where a card about
 * THIS booking's blouse belongs. This band is the other half: the neck she agreed to before the
 * order existed, which lives on her own record and is read here rather than copied. One copy of
 * an agreement, in one place, is the whole point — two copies is how a shop ends up cutting from
 * the version nobody updated.
 *
 * Read-only for the same reason: her board is hers, and this is somebody else's screen. The link
 * is how you get to the version you can change.
 *
 * It renders nothing at all when she has approved nothing, so an order booked at the counter with
 * no design conversation behind it is not given an empty region to explain.
 */
export function CustomerDesignsBand({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}): ReactElement | null {
  const { t } = useTranslation();

  // Approved only. "Idea" and "rejected" are the shop thinking out loud, and a tailor reading a
  // rejected neckline off the order screen is exactly the mistake this band exists to prevent.
  const cards = useDesignCards({ kind: 'customer', customerId }, { status: 'approved' });
  const items = cards.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground">
          {t('designBoard.onOrderHeading')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('designBoard.onOrderDescription', { name: customerName })}{' '}
          <Link to={`/customers/${customerId}`} className="underline">
            {t('designBoard.openHerBoard')}
          </Link>
        </p>
      </div>

      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((card) => (
          <DesignCardTile key={card.id} card={card} order={null} />
        ))}
      </ul>
    </section>
  );
}
